import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSellerQueue } from './hooks/useSellerQueue'
import { useSessionHistory } from './hooks/useSessionHistory'
import { useSellerQueuePluginspaceId } from './hooks/useSellerQueuePluginspaceId'

const REFRESH_INTERVAL_MS = 10000
const HIGHLIGHT_ROTATION_MS = 3000
const SOUND_STORAGE_KEY = 'seller-queue-panel-sound-enabled'

const getSellerDisplayName = (sellerId: number, sellerName?: string, userName?: string) => {
  return userName || sellerName || `Vendedor ${sellerId}`
}

const formatHour = (date = new Date()) => {
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const SellerPainelQueue: FC = () => {
  const pluginspaceId = useSellerQueuePluginspaceId()
  const { queue, loading, error: queueError, loadQueue } = useSellerQueue(pluginspaceId)
  const { sessions, error: sessionsError, loadHistory } = useSessionHistory(pluginspaceId)
  const [clock, setClock] = useState(formatHour())
  const [isFullscreen, setIsFullscreen] = useState<boolean>(!!document.fullscreenElement)
  const [highlightedQueueIndex, setHighlightedQueueIndex] = useState(0)
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    const storedValue = localStorage.getItem(SOUND_STORAGE_KEY)
    if (storedValue === null) return true
    return storedValue === 'true'
  })
  const previousCallingIdRef = useRef<number | null>(null)

  const waitingQueue = useMemo(() => {
    return [...queue]
      .filter((item) => item.status !== 'in_service')
      .sort((a, b) => a.position - b.position)
  }, [queue])

  const currentlyCalling = waitingQueue[0] || null
  const rotatingQueue = waitingQueue.slice(0, 6)

  const inProgressSessions = useMemo(() => {
    return sessions
      .filter((session) => session.status === 'in_progress')
      .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
  }, [sessions])

  const playCallSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

      const playBeep = (startOffset: number, frequency: number) => {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        const startAt = audioContext.currentTime + startOffset
        const endAt = startAt + 0.14

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(frequency, startAt)

        gainNode.gain.setValueAtTime(0.0001, startAt)
        gainNode.gain.exponentialRampToValueAtTime(0.14, startAt + 0.02)
        gainNode.gain.exponentialRampToValueAtTime(0.0001, endAt)

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.start(startAt)
        oscillator.stop(endAt)
      }

      // Padrão comercial de chamada: dois toques curtos com pequena pausa.
      playBeep(0, 950)
      playBeep(0.22, 880)

      window.setTimeout(() => {
        audioContext.close()
      }, 700)
    } catch (_) {
      // Ignore falhas de autoplay/permissão de áudio no navegador.
    }
  }, [])

  const handleToggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen()
      } else {
        await document.exitFullscreen()
      }
    } catch (_) {
      // Navegadores podem bloquear fullscreen fora de interação permitida.
    }
  }, [])

  useEffect(() => {
    const interval = window.setInterval(() => {
      setClock(formatHour())
      if (pluginspaceId > 0) {
        loadQueue()
        loadHistory()
      }
    }, REFRESH_INTERVAL_MS)

    return () => {
      window.clearInterval(interval)
    }
  }, [pluginspaceId, loadHistory, loadQueue])

  useEffect(() => {
    if (pluginspaceId > 0) {
      loadQueue()
      loadHistory()
    }
  }, [pluginspaceId, loadHistory, loadQueue])

  useEffect(() => {
    localStorage.setItem(SOUND_STORAGE_KEY, String(soundEnabled))
  }, [soundEnabled])

  useEffect(() => {
    const currentId = currentlyCalling?.id ?? null

    if (previousCallingIdRef.current === null) {
      previousCallingIdRef.current = currentId
      return
    }

    if (
      soundEnabled &&
      currentId !== null &&
      previousCallingIdRef.current !== null &&
      currentId !== previousCallingIdRef.current
    ) {
      playCallSound()
    }

    previousCallingIdRef.current = currentId
  }, [currentlyCalling?.id, playCallSound, soundEnabled])

  useEffect(() => {
    setHighlightedQueueIndex(0)
  }, [rotatingQueue.length])

  useEffect(() => {
    if (rotatingQueue.length <= 1) {
      return
    }

    const interval = window.setInterval(() => {
      setHighlightedQueueIndex((current) => (current + 1) % rotatingQueue.length)
    }, HIGHLIGHT_ROTATION_MS)

    return () => {
      window.clearInterval(interval)
    }
  }, [rotatingQueue.length])

  useEffect(() => {
    document.title = 'Painel da Fila de Vendedores'

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'f') {
        handleToggleFullscreen()
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleToggleFullscreen])

  return (
    <div
      className='card border-0 shadow-sm'
      style={{
        minHeight: '100vh',
        borderRadius: 0,
        background: 'linear-gradient(180deg, #f6fbff 0%, #ffffff 30%, #f8fffa 100%)',
      }}
    >
      <style>{`
        @keyframes panelPulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(54, 153, 255, 0.35); }
          70% { transform: scale(1.01); box-shadow: 0 0 0 18px rgba(54, 153, 255, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(54, 153, 255, 0); }
        }

        @keyframes callBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }

        @keyframes rowGlow {
          0% { background-color: #fff8e6; }
          50% { background-color: #fff1cc; }
          100% { background-color: #fff8e6; }
        }

        .queue-call-pulse {
          animation: panelPulse 2.6s infinite;
        }

        .queue-call-blink {
          animation: callBlink 1.15s infinite;
        }

        .queue-row-highlight {
          animation: rowGlow 1.4s ease-in-out infinite;
        }
      `}</style>

      <div className='card-body p-4 p-lg-6'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-6 gap-3'>
            <div>
              <h1
                className='mb-1'
                style={{
                  fontSize: isFullscreen ? '2.6rem' : '2rem',
                  fontWeight: 800,
                  letterSpacing: '0.02em',
                }}
              >
                Painel de Atendimento
              </h1>
              <p className='mb-0 text-muted' style={{ fontSize: isFullscreen ? '1.1rem' : '1rem' }}>
                Acompanhe em tempo real a fila e os vendedores em atendimento.
              </p>
            </div>
            <div className='text-md-end d-flex flex-wrap gap-2 justify-content-md-end'>
              <button
                type='button'
                className={`btn ${soundEnabled ? 'btn-light-success' : 'btn-light-danger'}`}
                onClick={() => setSoundEnabled((previous) => !previous)}
                style={{ fontWeight: 700 }}
              >
                <i className={`fas ${soundEnabled ? 'fa-volume-up' : 'fa-volume-mute'} me-2`}></i>
                {soundEnabled ? 'Som ligado' : 'Som desligado'}
              </button>
              <button
                type='button'
                className='btn btn-light-primary'
                onClick={handleToggleFullscreen}
                style={{ fontWeight: 700 }}
              >
                <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'} me-2`}></i>
                {isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              </button>
              <div className='badge badge-light-primary fs-5 px-4 py-3'>Atualizado: {clock}</div>
            </div>
          </div>

          <div className='alert alert-secondary d-flex align-items-center mb-5 py-3'>
            <i className='fas fa-tv me-2 text-dark'></i>
            Dica SmartTV: pressione <strong className='mx-1'>F</strong> para alternar o modo tela cheia e use o botão de som para mute/unmute.
          </div>

          {loading && (
            <div className='alert alert-info d-flex align-items-center mb-5'>
              <span className='spinner-border spinner-border-sm me-3' role='status' aria-hidden='true'></span>
              Carregando dados da fila...
            </div>
          )}

          {(queueError || sessionsError) && (
            <div className='alert alert-warning d-flex align-items-center mb-5'>
              <i className='fas fa-exclamation-triangle me-2'></i>
              Não foi possível carregar os dados do painel neste momento.
            </div>
          )}

          <div className='row g-4'>
            <div className='col-12 col-xl-7'>
              <div className='card h-100 border border-gray-300'>
                <div className='card-header bg-light-primary'>
                  <h3
                    className='card-title fw-bold text-primary m-0'
                    style={{ fontSize: isFullscreen ? '1.5rem' : '1.25rem' }}
                  >
                    Próximo Vendedor
                  </h3>
                </div>
                <div className='card-body'>
                  {currentlyCalling ? (
                    <div
                      className='rounded p-5 text-center mb-5 queue-call-pulse'
                      style={{
                        background: 'linear-gradient(135deg, #e8f3ff 0%, #f3f9ff 100%)',
                        border: '1px solid #b8dcff',
                      }}
                    >
                      <div className='text-muted mb-2 queue-call-blink' style={{ fontSize: isFullscreen ? '1.2rem' : '1rem' }}>
                        Próximo a ser chamado
                      </div>
                      <div
                        className='mb-2'
                        style={{
                          fontSize: isFullscreen ? '3rem' : '2.2rem',
                          fontWeight: 800,
                          lineHeight: 1.1,
                        }}
                      >
                        {getSellerDisplayName(
                          currentlyCalling.sellerId,
                          currentlyCalling.sellerName,
                          currentlyCalling.user?.name
                        )}
                      </div>
                      <div className='badge badge-primary px-4 py-2 queue-call-blink' style={{ fontSize: isFullscreen ? '1rem' : '0.85rem' }}>
                        Posição {currentlyCalling.position}
                      </div>
                    </div>
                  ) : (
                    <div className='text-center text-muted py-10' style={{ fontSize: isFullscreen ? '1.2rem' : '1rem' }}>
                      Nenhum vendedor na fila no momento.
                    </div>
                  )}

                  <h4 className='fw-bold mb-4' style={{ fontSize: isFullscreen ? '1.4rem' : '1.1rem' }}>
                    Fila de Espera
                  </h4>
                  {waitingQueue.length > 0 ? (
                    <div className='table-responsive'>
                      <table className='table table-row-dashed table-row-gray-300 align-middle gy-4'>
                        <thead>
                          <tr className='fw-bold text-gray-700' style={{ fontSize: isFullscreen ? '1.05rem' : '0.9rem' }}>
                            <th>Posição</th>
                            <th>Vendedor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {waitingQueue.slice(0, 12).map((item, index) => {
                            const isHighlighted = rotatingQueue.length > 0 && index === highlightedQueueIndex
                            return (
                            <tr key={item.id} className={isHighlighted ? 'queue-row-highlight' : ''}>
                              <td>
                                <span className='badge badge-light-dark' style={{ fontSize: isFullscreen ? '1rem' : '0.85rem' }}>
                                  #{item.position}
                                </span>
                              </td>
                              <td className='fw-semibold' style={{ fontSize: isFullscreen ? '1.15rem' : '0.95rem' }}>
                                {getSellerDisplayName(item.sellerId, item.sellerName, item.user?.name)}
                              </td>
                            </tr>
                          )})}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className='text-muted mb-0' style={{ fontSize: isFullscreen ? '1.1rem' : '1rem' }}>
                      Fila vazia.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className='col-12 col-xl-5'>
              <div className='card h-100 border border-gray-300'>
                <div className='card-header bg-light-success'>
                  <h3
                    className='card-title fw-bold text-success m-0'
                    style={{ fontSize: isFullscreen ? '1.5rem' : '1.25rem' }}
                  >
                    Em Atendimento
                  </h3>
                </div>
                <div className='card-body'>
                  {inProgressSessions.length === 0 ? (
                    <div className='text-center text-muted py-10' style={{ fontSize: isFullscreen ? '1.2rem' : '1rem' }}>
                      Nenhum atendimento em andamento.
                    </div>
                  ) : (
                    <div className='d-flex flex-column gap-3'>
                      {inProgressSessions.slice(0, 10).map((session) => (
                        <div
                          key={session.id}
                          className='rounded p-4'
                          style={{
                            backgroundColor: '#f5fff7',
                            border: '1px solid #c9f7d5',
                          }}
                        >
                          <div className='d-flex justify-content-between align-items-center mb-1'>
                            <strong style={{ fontSize: isFullscreen ? '1.25rem' : '1.05rem' }}>
                              {session.sellerName || `Vendedor ${session.sellerId}`}
                            </strong>
                            <span className='badge badge-light-success' style={{ fontSize: isFullscreen ? '0.95rem' : '0.8rem' }}>
                              Ativo
                            </span>
                          </div>
                          <small className='text-muted' style={{ fontSize: isFullscreen ? '1rem' : '0.85rem' }}>
                            Iniciado em {new Date(session.startedAt).toLocaleTimeString('pt-BR')}
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  )
}

export { SellerPainelQueue }